import { useState } from 'react'
import type { FeedbackState, FeedbackSubmission } from '@/hooks/useFeedback'
import styles from './FeedbackControls.module.css'

// Mirrors src/answer_service/adapters/driver/teams/feedback_cards.py.
// Kept aligned so the SPA collects the same buckets the Teams adapter
// has been collecting since launch — switching the wire format mid-cutover
// would break dashboards.
const REASON_OPTIONS = [
  'Feil kilder brukt',
  'Besvarte ikke spørsmålet mitt',
  'For vagt/generelt',
  'For teknisk/kompleks',
  'Faktisk feil informasjon',
  'Annet',
] as const

const CAUSE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Agent', label: 'Agent-logikk (feil tilnærming)' },
  { value: 'Spørsmålsformulering', label: 'Spørsmålsformulering (uklart spørsmål)' },
  { value: 'Dokumenter tilgjengelige', label: 'Tilgjengelige dokumenter (mangler info)' },
  { value: 'Søkekvalitet', label: 'Søkekvalitet (feil dokumenter funnet)' },
  { value: 'Usikker', label: 'Usikker' },
]

export type FeedbackControlsProps = {
  state: FeedbackState
  onSubmit: (submission: FeedbackSubmission) => Promise<void>
}

const DEFAULT_REASON: string = REASON_OPTIONS[0]
const DEFAULT_CAUSE: string = CAUSE_OPTIONS[0]?.value ?? ''

export default function FeedbackControls({ state, onSubmit }: FeedbackControlsProps) {
  const [showDetail, setShowDetail] = useState(false)
  const [reason, setReason] = useState<string>(DEFAULT_REASON)
  const [cause, setCause] = useState<string>(DEFAULT_CAUSE)
  const [comment, setComment] = useState<string>('')

  if (state.kind === 'submitted') {
    return (
      <p className={styles.thanks} role="status">
        Takk for tilbakemeldingen!
      </p>
    )
  }

  const positiveActive = state.kind === 'submitting' && state.rating === 'positive'
  const negativeActive = state.kind === 'submitting' && state.rating === 'negative'
  const submitting = state.kind === 'submitting'

  const handlePositive = async () => {
    await onSubmit({ rating: 'positive' })
  }

  const handleNegativeClick = () => {
    setShowDetail(true)
  }

  const handleNegativeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await onSubmit({ rating: 'negative', reason, cause, comment })
    setShowDetail(false)
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        <button
          type="button"
          aria-pressed={positiveActive}
          aria-label="Var nyttig"
          disabled={submitting}
          onClick={() => void handlePositive()}
          className={`${styles.button} ${positiveActive ? styles.active : ''}`}
        >
          👍 Nyttig
        </button>
        <button
          type="button"
          aria-pressed={negativeActive || showDetail}
          aria-label="Var ikke nyttig"
          disabled={submitting}
          onClick={handleNegativeClick}
          className={`${styles.button} ${negativeActive || showDetail ? styles.active : ''}`}
        >
          👎 Ikke nyttig
        </button>
        {state.kind === 'error' && (
          <span className={styles.error} role="alert">
            Kunne ikke sende: {state.message}
          </span>
        )}
      </div>

      {showDetail && state.kind !== 'submitting' && (
        <form className={styles.detailForm} onSubmit={(e) => void handleNegativeSubmit(e)}>
          <label className={styles.field}>
            <span className={styles.label}>Hvorfor var svaret dårlig?</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={styles.select}
            >
              {REASON_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Hva tror du var årsaken?</span>
            <select
              value={cause}
              onChange={(e) => setCause(e.target.value)}
              className={styles.select}
            >
              {CAUSE_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Tilleggskommentar (valgfritt)</span>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={3}
              className={styles.textarea}
              placeholder="Skriv eventuell tilleggskommentar her…"
            />
          </label>
          <div className={styles.actions}>
            <button type="submit" className={styles.submitButton}>
              Send tilbakemelding
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setShowDetail(false)}
            >
              Avbryt
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
